import React, { useCallback, useEffect, useSyncExternalStore } from "react";
import useEmblaCarousel from "embla-carousel-react";

type ImageSliderProps = {
    images: string[];
    alt?: string;
    className?: string;
    imageClassName?: string;
};

/**
 * 画像スライダーコンポーネント
 *
 * Embla Carouselを使用して、複数の画像をスライド形式で表示します。
 * ナビゲーションボタン（前/次）とインジケーター（ドット）を表示します。
 */
export const ImageSlider: React.FC<ImageSliderProps> = ({
    images,
    alt,
    className,
    imageClassName,
}) => {
    const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true });

    // 5秒ごとに自動で次のスライドへ移動（操作・スクロール時にリセット）
    useEffect(() => {
        if (!emblaApi) return;

        let timerId: number | null = null;

        const startAutoPlay = () => {
            // 既存タイマーをクリアしてから再セット
            if (timerId !== null) {
                window.clearTimeout(timerId);
            }
            timerId = window.setTimeout(() => {
                if (!emblaApi) return;
                emblaApi.scrollNext();
            }, 5000);
        };

        // スライドが変わったり、ユーザー操作があったらタイマーをリセット
        const handleInteraction = () => {
            startAutoPlay();
        };

        emblaApi.on("select", handleInteraction);
        emblaApi.on("pointerDown", handleInteraction);
        emblaApi.on("pointerUp", handleInteraction);

        // 初期起動
        startAutoPlay();

        return () => {
            if (timerId !== null) {
                window.clearTimeout(timerId);
            }
            emblaApi.off("select", handleInteraction);
            emblaApi.off("pointerDown", handleInteraction);
            emblaApi.off("pointerUp", handleInteraction);
        };
    }, [emblaApi]);

    const selectedIndex = useSyncExternalStore(
        (onStoreChange) => {
            if (!emblaApi) {
                return () => undefined;
            }
            emblaApi.on("select", onStoreChange);
            return () => emblaApi.off("select", onStoreChange);
        },
        () => emblaApi?.selectedScrollSnap() ?? 0,
        () => 0
    );

    // 指定したインデックスのスライドに直接移動する関数
    const scrollTo = useCallback(
        (index: number) => emblaApi?.scrollTo(index),
        [emblaApi]
    );

    // 画像が存在しない場合は何も表示しない
    if (!images || images.length === 0) return null;

    // 複数の画像がある場合のみ、ナビゲーションボタンとインジケーターを表示
    const hasMultipleImages = images.length > 1;

    return (
        <div className={`relative ${className ?? ""}`}>
            <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex">
                    {images.map((image, index) => (
                        <div
                            key={index}
                            className="min-w-0 flex-shrink-0 flex-grow-0 basis-full"
                        >
                            <img
                                src={image}
                                alt={
                                    alt
                                        ? `${alt} ${index + 1}`
                                        : `スライド ${index + 1}`
                                }
                                className={imageClassName}
                            />
                        </div>
                    ))}
                </div>
            </div>

            {hasMultipleImages && (
                <div className="mt-4 flex justify-center gap-2">
                    {images.map((_, index) => (
                        <button
                            key={index}
                            className={`h-2 rounded-full transition-all ${
                                index === selectedIndex
                                    ? "w-6 bg-gray-600"
                                    : "w-2 bg-gray-300"
                            }`}
                            onClick={() => scrollTo(index)}
                            aria-label={`スライド ${index + 1} に移動`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};
